import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const HowItWorks = () => {
  return (
    <>
    <SEOHead
      title="How It Works"
      description="See how Cayda works in three steps: add your site, install one script, and start leaving visual feedback on live pages. 30 second setup."
      path="/how-it-works"
    />
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">How It Works</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight mb-5">
          <span className="font-serif italic text-ink/80">How Cayda works.</span>{' '}
          <span className="font-display font-bold text-ink">Three steps. Thirty seconds.</span>
        </h1>
        <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">
          Cayda adds an invisible feedback layer to your website. Your team leaves notes directly on live pages. Your visitors never know it's there.
        </p>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="space-y-14 text-[15px] font-display text-ink-secondary leading-relaxed">

          {/* Step 1 */}
          <section>
            <div className="flex items-start gap-4 mb-4">
              <span className="text-4xl font-serif text-ink-faint leading-none mt-1">01</span>
              <div>
                <h2 className="text-lg font-display font-semibold text-ink mb-1">Add your site</h2>
                <p className="text-ink-muted text-sm">Takes about 5 seconds</p>
              </div>
            </div>
            <p className="mb-3">
              Sign up for a free account and paste your website URL into the Cayda dashboard. That's it, Cayda creates a workspace for your site and generates a unique embed token.
            </p>
            <p>
              You can add multiple sites. Free accounts get 2. Pro and Team accounts get unlimited. Each site has its own notes, team members, and settings.
            </p>
          </section>

          {/* Step 2 */}
          <section>
            <div className="flex items-start gap-4 mb-4">
              <span className="text-4xl font-serif text-ink-faint leading-none mt-1">02</span>
              <div>
                <h2 className="text-lg font-display font-semibold text-ink mb-1">Install one script</h2>
                <p className="text-ink-muted text-sm">One line of code, or one click for WordPress and Shopify</p>
              </div>
            </div>
            <p className="mb-3">
              Copy a single script tag from your dashboard and paste it into your site's HTML before the closing <code className="text-xs font-mono bg-white border border-ink-faint/20 rounded px-1.5 py-0.5">&lt;/body&gt;</code> tag. The script loads asynchronously and has no meaningful impact on page speed.
            </p>
            <div className="bg-[#0F0F0F] rounded-xl p-5 my-5 overflow-x-auto">
              <code className="text-[13px] leading-relaxed font-mono">
                <span className="text-ink-muted">&lt;</span>
                <span className="text-amber-400">script</span>
                {' '}
                <span className="text-cyan-400">src</span>
                <span className="text-ink-muted">=</span>
                <span className="text-emerald-400">"https://api.cayda.io/widget.js"</span>
                {'\n  '}
                <span className="text-cyan-400">data-token</span>
                <span className="text-ink-muted">=</span>
                <span className="text-emerald-400">"your-token"</span>
                {' '}
                <span className="text-cyan-400">defer</span>
                <span className="text-ink-muted">&gt;&lt;/</span>
                <span className="text-amber-400">script</span>
                <span className="text-ink-muted">&gt;</span>
              </code>
            </div>
            <p className="mb-3">
              For WordPress and Shopify, it's even simpler. WordPress gets a one click plugin install, and Shopify has a native app in the Shopify App Store. No code editing required for either.
            </p>
            <p>
              Cayda also works with Squarespace, Webflow, Wix, Next.js, React, Vue, and plain HTML. It handles single page apps, page transitions, and dynamic content automatically. See the full <Link to="/compatibility" className="text-accent hover:text-accent-hover underline underline-offset-2 transition-colors">compatibility guide</Link>.
            </p>
          </section>

          {/* Step 3 */}
          <section>
            <div className="flex items-start gap-4 mb-4">
              <span className="text-4xl font-serif text-ink-faint leading-none mt-1">03</span>
              <div>
                <h2 className="text-lg font-display font-semibold text-ink mb-1">Click to annotate</h2>
                <p className="text-ink-muted text-sm">Notes on the page in seconds</p>
              </div>
            </div>
            <p className="mb-3">
              Visit your site and log in through the Cayda pill, the small floating button that appears for authenticated users. Enter annotation mode, click on any element, and type your note. Choose a type (bug, design, content, question, or feedback) and save. Optionally, you can set a priority level, due date, assignee and more.
            </p>
            <p>
              The note is now pinned to that element. Anyone on your team who visits the page will see it as a colored marker on the element, with the full comment available on hover or click. Visitors see nothing.
            </p>
          </section>

          {/* What happens next */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">What happens after that</h2>
            <p className="mb-3">
              Notes flow into the Cayda dashboard - a centralized view of every annotation across all your sites. From there, you can filter by status, priority, note type, assignee, or page URL. View them as a list or a Kanban board. Assign them to team members. Resolve them as issues get fixed.
            </p>
            <p>
              If you've connected Slack, new notes trigger real time notifications in your chosen channel. If you've connected Jira or Asana, you can push any note into your project management tool with one click.
            </p>
          </section>

          {/* Invite your team */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Invite your team</h2>
            <p>
              Add teammates as collaborators on any site. They'll receive an email invitation and can start leaving notes immediately after accepting. Each collaborator can leave notes, reply in threads, and update statuses. On paid plans, you can also assign viewer roles with read only access for stakeholders who need to see progress without creating notes.
            </p>
          </section>

          {/* What's captured */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">What every note captures</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { label: 'Screenshot', desc: 'Full page screenshot at the moment the note was created.' },
                { label: 'Page URL', desc: 'The exact URL, including query params and hash.' },
                { label: 'Element selector', desc: 'CSS selector for the pinned element.' },
                { label: 'Viewport', desc: 'Browser window dimensions at the time.' },
                { label: 'Browser & OS', desc: 'Chrome 120, macOS 14.2, etc.' },
                { label: 'Device type', desc: 'Desktop, tablet, or mobile.' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-white border border-ink-faint/20 rounded-lg p-3.5">
                  <p className="text-xs font-display font-semibold text-ink mb-0.5">{label}</p>
                  <p className="text-[11px] font-display text-ink-muted leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats bar */}
          <section>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-mono text-ink-muted py-6 border-y border-ink-faint/20">
              <span><span className="text-ink font-semibold">30s</span> install</span>
              <span className="text-ink-faint">·</span>
              <span><span className="text-ink font-semibold">0</span> speed impact for visitors</span>
              <span className="text-ink-faint">·</span>
              <span><span className="text-ink font-semibold">0</span> cookies for visitors</span>
            </div>
          </section>
        </div>

        <div className="mt-14"><RelatedLinks current="/how-it-works" /></div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Ready to try it?</h3>
          <p className="text-sm font-display text-ink-muted mb-5">Free plan. No credit card. Under two minutes to set up.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
            <Link to="/compatibility" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium border border-ink-faint/40 hover:border-ink-faint text-ink-secondary hover:text-ink rounded-lg transition-colors">Check platform compatibility</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-faint/20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default HowItWorks;
